import { FC, useCallback, useRef, useState } from 'react';
import type { CalendarioRecorte, RecortePainel } from '@/types/dashboards';
import {
  ATALHOS_PERIODO,
  CALENDARIOS,
  PERIODO_PERSONALIZADO,
  type FiltroPaineis,
} from '@/utils/filtroPaineis';
import { fmtDiaMes } from '@/utils/painelFormat';
import CalendarioPeriodo from './CalendarioPeriodo';
import { useFechaFora } from '@/hooks/useFechaFora';
import Popover from './Popover';

interface Props {
  filtro: FiltroPaineis;
  /** O que o servidor aplicou: marca o atalho padrão quando nada foi escolhido. */
  recorte?: RecortePainel;
  onChange: (filtro: FiltroPaineis) => void;
  /** A aba vai sempre por mês comum (a DRE): o seletor de tipo de mês some. */
  semCalendario?: boolean;
}

const chip = (ativo: boolean) =>
  `inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
    ativo
      ? 'border-accent bg-accent font-semibold text-accent-contrast'
      : 'border-theme-border text-content-secondary hover:text-content-primary'
  }`;

const Personalizado: FC<Props> = ({ filtro, recorte, onChange }) => {
  const [aberto, setAberto] = useState(false);
  const ancora = useRef<HTMLDivElement>(null);
  const fechar = useCallback(() => setAberto(false), []);
  useFechaFora(ancora, aberto, fechar);
  const ativo = filtro.periodo === PERIODO_PERSONALIZADO && !!filtro.inicio && !!filtro.fim;

  // Sem `relative` aqui: o calendário se posiciona pela barra de filtros (ver Popover).
  return (
    <div ref={ancora}>
      <button
        type="button"
        aria-pressed={ativo}
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
        data-testid="filtro-personalizado"
        className={chip(ativo)}
      >
        <span aria-hidden className="material-symbols-outlined text-[14px] leading-none">
          calendar_month
        </span>
        {ativo ? `${fmtDiaMes(filtro.inicio as string)} a ${fmtDiaMes(filtro.fim as string)}` : 'Escolher datas'}
      </button>
      {aberto && (
        <Popover rotulo="Escolher o período no calendário">
          <CalendarioPeriodo
            inicio={filtro.inicio}
            fim={filtro.fim}
            janela={recorte?.janela ?? null}
            onCancelar={fechar}
            onAplicar={(inicio, fim) => {
              onChange({ ...filtro, periodo: PERIODO_PERSONALIZADO, inicio, fim });
              fechar();
            }}
          />
        </Popover>
      )}
    </div>
  );
};

/**
 * Período do filtro: atalhos (resolvidos no servidor, relativos a hoje), "escolher datas"
 * no calendário e o tipo de mês — de produção (16 a 15) ou comum (1 ao fim) —, que vale
 * para os atalhos de mês e para o agrupamento mês a mês.
 */
const SeletorPeriodo: FC<Props> = ({ filtro, recorte, onChange, semCalendario = false }) => {
  const vigente = filtro.periodo ?? recorte?.periodo ?? null;
  const escolher = (periodo: string) => onChange({ ...filtro, periodo, inicio: null, fim: null });
  const trocarCalendario = (calendario: CalendarioRecorte) => onChange({ ...filtro, calendario });

  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label="Período" className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs uppercase tracking-wide text-content-muted">Período</span>
        {ATALHOS_PERIODO.map((a) => (
          <button
            key={a.id}
            type="button"
            aria-pressed={vigente === a.id}
            onClick={() => escolher(a.id)}
            className={chip(vigente === a.id)}
          >
            {a.rotulo}
            {filtro.periodo === null && recorte?.periodo === a.id && <span className="font-normal">· padrão</span>}
          </button>
        ))}
        <Personalizado filtro={filtro} recorte={recorte} onChange={onChange} />
      </div>
      {!semCalendario && (
        <div role="group" aria-label="Tipo de mês" className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs uppercase tracking-wide text-content-muted">Tipo de mês</span>
          {CALENDARIOS.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={filtro.calendario === c.id}
              onClick={() => trocarCalendario(c.id)}
              title={c.detalhe}
              className={chip(filtro.calendario === c.id)}
            >
              {c.rotulo}
              <span className="font-normal">· {c.detalhe}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SeletorPeriodo;
